import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import InviteCounterpartyModal from '../components/InviteCounterpartyModal';
import PendingInvitationsPanel from '../components/PendingInvitationsPanel';
import { connectionsApi } from '../utils/api';

const stateStyles = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  severed: 'bg-gray-100 text-gray-600 border-gray-200',
};

export default function Connections() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  // PRD-010 F-PI-3: bump this counter to refresh the pending-invitations
  // panel after a new invite is sent or after a connection accept changes
  // the world.
  const [pendingRefresh, setPendingRefresh] = useState(0);

  const fetchConnections = async () => {
    setLoading(true);
    try {
      const res = await connectionsApi.list();
      setConnections(res.data.connections);
    } catch (err) {
      console.error('Failed to fetch connections:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConnections(); }, []);

  const handleAccept = async (id) => {
    try {
      await connectionsApi.accept(id);
      await fetchConnections();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to accept connection');
    }
  };

  const handleSever = async (id) => {
    if (!window.confirm('Sever this connection? Existing Agreements stay intact; new Proposals can no longer be drafted.')) return;
    try {
      await connectionsApi.sever(id);
      await fetchConnections();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to sever connection');
    }
  };

  const grouped = {
    active: connections.filter((c) => c.state === 'active'),
    pending: connections.filter((c) => c.state === 'pending'),
    severed: connections.filter((c) => c.state === 'severed'),
  };

  const visible = grouped[activeTab];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="page-header">Connections</h1>
            <p className="text-gray-600 mt-1">Commercial relationships with counterparty workspaces</p>
          </div>
          <div className="flex items-center gap-3 mt-4 sm:mt-0">
            {/* PRD-016: in-app "Discover workspaces" directory removed — Connections form via email-link invitations only. */}
            <button
              onClick={() => setInviteOpen(true)}
              className="btn-primary"
            >
              + Invite counterparty
            </button>
          </div>
        </div>

        <PendingInvitationsPanel refreshTrigger={pendingRefresh} />

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-gray-200">
          {['active', 'pending', 'severed'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-600 hover:text-gray-700'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              <span className="ml-2 text-xs text-gray-600">{grouped[tab].length}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          </div>
        ) : visible.length === 0 ? (
          <div className="card p-8 text-center text-gray-600">
            No {activeTab} connections.
            {activeTab === 'active' && (
              <p className="mt-2 text-sm">
                Invite a counterparty to get started.
              </p>
            )}
          </div>
        ) : (
          <div className="card overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Counterparty</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Direction</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">State</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Last activity</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visible.map((c) => {
                  const counterparty = c.myRole === 'seller' ? c.buyerWorkspace : c.sellerWorkspace;
                  const lastActivity = c.severedAt || c.acceptedAt || c.initiatedAt;
                  const canAccept = c.state === 'pending' && c.initiatorWorkspaceId !== counterparty.id;
                  const canSever = c.state !== 'severed';
                  return (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <Link to={`/connections/${c.id}`} className="text-primary-600 hover:text-primary-700 font-medium">
                          {counterparty.name}
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {c.myRole === 'seller' ? 'Selling to' : 'Buying from'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full border ${stateStyles[c.state]}`}>
                          {c.state}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {lastActivity ? new Date(lastActivity).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-6 py-4 text-right space-x-3">
                        {canAccept && (
                          <button onClick={() => handleAccept(c.id)} className="text-xs text-primary-600 hover:text-primary-700">
                            Accept
                          </button>
                        )}
                        {canSever && (
                          <button onClick={() => handleSever(c.id)} className="text-xs text-red-600 hover:text-red-700">
                            Sever
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {inviteOpen && (
        <InviteCounterpartyModal
          onClose={() => setInviteOpen(false)}
          onInvited={() => {
            setInviteOpen(false);
            fetchConnections();
            setPendingRefresh((v) => v + 1);
          }}
        />
      )}
    </Layout>
  );
}
